if (!customElements.get('custom-laminate-table-builder')) {
  customElements.define(
    'custom-laminate-table-builder',
    class CustomLaminateTableBuilder extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.config = JSON.parse(this.querySelector('[data-builder-config]').textContent);
        this.config.variants = [
          ...(this.config.primaryVariants || []),
          ...(this.config.premiumVariants || []),
          ...(this.config.premiumVariants2 || []),
        ];
        this.lengthInput = this.querySelector('[data-length]');
        this.depthInput = this.querySelector('[data-depth]');
        this.productCache = new Map();
        this.submitButton = this.querySelector('[data-submit]');

        this.lengthInput.value = this.config.dimensions.defaultLength;
        this.depthInput.value = this.config.dimensions.defaultDepth;
        this.addEventListener('input', this.onInput.bind(this));
        this.addEventListener('change', this.onInput.bind(this));
        this.submitButton.addEventListener('click', this.addToCart.bind(this));
        this.onInput();
      }

      onInput() {
        const frameKey = this.selected('frame');
        const controlPanelSelect = this.querySelector('[data-control-panel]');
        const staticFrame = frameKey === 'static';
        const tactileOnlyFrame = frameKey === 'essential' || frameKey === 'pro';
        const apexOnlyOption = controlPanelSelect.querySelector('[data-apex-only]');
        if (apexOnlyOption) apexOnlyOption.disabled = frameKey !== 'apex';
        if (staticFrame) {
          controlPanelSelect.value = 'not_applicable';
          controlPanelSelect.disabled = true;
        } else if (tactileOnlyFrame) {
          controlPanelSelect.value = 'tactile_keys';
          controlPanelSelect.disabled = true;
        } else {
          controlPanelSelect.disabled = false;
          if (controlPanelSelect.value === 'not_applicable') controlPanelSelect.value = 'tactile_keys';
          if (controlPanelSelect.value === 'paddle_shift' && frameKey !== 'apex') controlPanelSelect.value = 'tactile_keys';
        }

        this.querySelectorAll('[data-addon-select][data-quantity-target]').forEach((select) => {
          const quantity = this.querySelector(`[data-select-quantity="${select.dataset.quantityTarget}"]`);
          if (!quantity) return;
          const enabled = Boolean(select.value);
          quantity.disabled = !enabled;
          if (enabled && this.quantity(quantity) < 1) quantity.value = 1;
        });

        const cableSelect = this.querySelector('[data-cable-addon]');
        if (cableSelect) {
          const enabled = Boolean(cableSelect.value);
          const colour = this.querySelector('[data-cable-colour]');
          const quantity = this.querySelector('[data-cable-quantity]');
          colour.disabled = !enabled;
          quantity.disabled = !enabled;
          if (enabled && this.quantity(quantity) < 1) quantity.value = 1;
        }
        this.calculate();
      }

      numberValue(input) {
        return Number.parseInt(input.value, 10);
      }

      quantity(input) {
        const value = this.numberValue(input);
        return Number.isFinite(value) && value > 0 ? value : 0;
      }

      selected(name) {
        return this.querySelector(`input[name^="ctb-${name}-"]:checked`)?.value;
      }

      selectedAddOns() {
        const quantities = new Map();
        const add = (key, quantity = 1) => {
          if (!key || !this.config.addOns[key] || quantity <= 0) return;
          quantities.set(key, (quantities.get(key) || 0) + quantity);
        };

        this.querySelectorAll('[data-addon-select]').forEach((select) => {
          const target = select.dataset.quantityTarget;
          const quantity = target
            ? this.quantity(this.querySelector(`[data-select-quantity="${target}"]`))
            : 1;
          add(select.value, target ? Math.max(1, quantity) : quantity);
        });
        this.querySelectorAll('[data-addon-checkbox]:checked').forEach((input) => add(input.dataset.addonCheckbox));
        this.querySelectorAll('[data-addon-quantity]').forEach((input) => add(input.dataset.addonQuantity, this.quantity(input)));

        const controlPanel = this.config.controlPanels[this.querySelector('[data-control-panel]').value];
        if (controlPanel?.addOnKey) add(controlPanel.addOnKey);

        const cableKey = this.querySelector('[data-cable-addon]')?.value;
        const cableQuantity = this.querySelector('[data-cable-quantity]');
        if (cableKey) add(cableKey, Math.max(1, this.quantity(cableQuantity)));

        return Array.from(quantities, ([key, quantity]) => ({
          key,
          quantity,
          colour: key === cableKey ? this.querySelector('[data-cable-colour]').value : null,
          ...this.config.addOns[key],
        }));
      }

      calculate() {
        this.clearError();
        const length = this.numberValue(this.lengthInput);
        const depth = this.numberValue(this.depthInput);
        const dimensions = this.config.dimensions;
        this.validationMessage = '';

        if (
          !Number.isInteger(length) ||
          !Number.isInteger(depth) ||
          length < dimensions.lengthMin ||
          length > dimensions.lengthMax ||
          depth < dimensions.depthMin ||
          depth > dimensions.depthMax
        ) {
          this.result = null;
          this.querySelector('[data-area]').textContent = '—';
          this.querySelector('[data-base-price]').textContent = '—';
          this.querySelector('[data-options-price]').textContent = this.money(0);
          this.querySelector('[data-cable-price]').textContent = this.money(0);
          this.querySelector('[data-total-price]').textContent = '—';
          return;
        }

        const materialKey = this.querySelector('[data-material]').value;
        const frameKey = this.selected('frame');
        const frameColour = this.querySelector('[data-frame-colour]').value;
        const controlPanel = this.config.controlPanels[this.querySelector('[data-control-panel]').value];
        const material = this.config.materials[materialKey];
        const frame = this.config.frames[frameKey];
        const essentialOutOfRange = frameKey === 'essential' && (length > 160 || depth < 58 || depth > 80);
        if (essentialOutOfRange) {
          this.result = null;
          this.validationMessage = 'ESSENTIAL supports lengths from 75–160 cm and depths from 58–80 cm.';
          this.querySelector('[data-area]').textContent = `${(length * depth / 10000).toFixed(4).replace(/0+$/, '').replace(/\.$/, '')} m²`;
          this.querySelector('[data-base-price]').textContent = '—';
          this.querySelector('[data-options-price]').textContent = this.money(0);
          this.querySelector('[data-cable-price]').textContent = this.money(0);
          this.querySelector('[data-total-price]').textContent = '—';
          return;
        }
        this.validationMessage = '';
        const pricing = this.config.laminatePricing;
        const areaCm2 = length * depth;
        const areaM2 = areaCm2 / 10000;
        const basePrice = pricing.basePrice
          + Math.max(0, length - pricing.includedLength) * pricing.lengthIncrement
          + Math.max(0, depth - pricing.includedDepth) * pricing.depthIncrement
          + frame.price;

        const selectedAddOns = this.selectedAddOns();
        const optionsPrice = selectedAddOns.reduce((total, addOn) => total + addOn.price * addOn.quantity, 0);
        const cablePrice = selectedAddOns
          .filter((addOn) => addOn.colour)
          .reduce((total, addOn) => total + addOn.price * addOn.quantity, 0);
        const customisationsPrice = optionsPrice - cablePrice;

        this.result = {
          length,
          depth,
          areaCm2,
          areaM2,
          materialKey,
          material,
          frameKey,
          frame,
          frameColour,
          controlPanel,
          basePrice,
          optionsPrice,
          customisationsPrice,
          cablePrice,
          totalPrice: basePrice + optionsPrice,
          selectedAddOns,
        };

        this.querySelector('[data-area]').textContent = `${areaM2.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')} m²`;
        this.querySelector('[data-base-price]').textContent = this.money(basePrice);
        this.querySelector('[data-options-price]').textContent = this.money(customisationsPrice);
        this.querySelector('[data-cable-price]').textContent = this.money(cablePrice);
        this.querySelector('[data-total-price]').textContent = this.money(basePrice + optionsPrice);
      }

      money(amount) {
        return new Intl.NumberFormat('en-SG', {
          style: 'currency',
          currency: this.config.currency || 'SGD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(amount);
      }

      baseVariant() {
        if (!this.result) return null;
        const targetCents = Math.round(this.result.basePrice * 100);
        return this.config.variants.find((variant) => Number(variant.price) === targetCents && variant.available);
      }

      async addOnVariant(addOn) {
        if (this.productCache.has(addOn.handle)) return this.productCache.get(addOn.handle);
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}products/${addOn.handle}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`${addOn.label} product is unavailable. Please check the customisation product import.`);
        const product = await response.json();
        const variant = product.variants?.find((item) => item.available) || product.variants?.[0];
        if (!variant) throw new Error(`${addOn.label} has no available Shopify variant.`);
        this.productCache.set(addOn.handle, variant);
        return variant;
      }

      async addOnItem(addOn, tableReference) {
        const variant = await this.addOnVariant(addOn);
        return {
          id: variant.id,
          quantity: addOn.quantity,
          properties: {
            'For custom table': tableReference,
            ...(addOn.colour ? { 'Colour': addOn.colour } : {}),
          },
        };
      }

      async addToCart() {
        this.calculate();
        if (!this.result) {
          this.showError(this.validationMessage || 'Please enter valid whole-centimetre dimensions within the allowed range.');
          return;
        }

        const baseVariant = this.baseVariant();
        if (!baseVariant) {
          this.showError(`The ${this.money(this.result.basePrice)} price variant is unavailable. Please check the imported price variants.`);
          return;
        }

        const r = this.result;
        const reference = `${r.length} × ${r.depth} cm · ${r.material.label} · ${r.frame.label} (${r.frameColour})`;
        const properties = {
          'Length': `${r.length} cm`,
          'Depth': `${r.depth} cm`,
          'Area': `${r.areaM2.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')} m²`,
          'Tabletop material': r.material.label,
          'Table frame': r.frame.label,
          'Frame colour': r.frameColour,
          'Control panel': r.controlPanel.label,
          'Front profile': 'Straight Edge',
          'Customisation remarks': this.querySelector('[data-remarks]').value.trim() || 'None',
        };
        r.selectedAddOns.forEach((addOn) => {
          const quantityText = addOn.quantity > 1 ? `Qty: ${addOn.quantity}` : 'Yes';
          properties[addOn.label] = addOn.colour ? `${quantityText} · ${addOn.colour}` : quantityText;
        });

        try {
          this.setLoading(true);
          const addOnItems = await Promise.all(r.selectedAddOns.map((addOn) => this.addOnItem(addOn, reference)));
          const items = [
            { id: baseVariant.id, quantity: 1, properties },
            ...addOnItems,
          ];

          const payload = { items };

          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(payload),
          });
          const data = await response.json();
          if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add this table to cart.');

          window.location.href = window.routes?.cart_url || '/cart';
        } catch (error) {
          this.showError(error.message || 'Unable to add this table to cart. Please try again.');
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(loading) {
        this.submitButton.toggleAttribute('aria-disabled', loading);
        this.submitButton.disabled = loading;
        this.submitButton.classList.toggle('loading', loading);
        this.submitButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
      }

      showError(message) {
        const wrapper = this.querySelector('[data-error]');
        wrapper.hidden = false;
        wrapper.querySelector('.product-form__error-message').textContent = message;
      }

      clearError() {
        const wrapper = this.querySelector('[data-error]');
        wrapper.hidden = true;
        wrapper.querySelector('.product-form__error-message').textContent = '';
      }
    }
  );
}
